/**
 * The set-password link's token, taken off the address before anything records
 * it.
 *
 * The token arrives after `#`, so no server is ever sent it. The browser keeps
 * it, though: in the tab's history, and, because error reporting starts with
 * the page, in every error report, trace and session replay the page sends. Any
 * of those could set the account's password while the link lives. So it is
 * taken off the address on arrival with `replaceState`, which adds no history
 * entry, as `takeHandoffCode` does for a launch's code.
 *
 * Kept in `sessionStorage`, which is this tab's alone and is sent nowhere, so a
 * reload still finds it; without it, a reload would call a live link dead. If
 * the tab cannot store it, it is kept for as long as the page is open, and an
 * older token the tab stored before is dropped, so it never answers in its
 * place. It is kept only while the tab is on the page: any other page of the
 * app, loaded or reached within it, forgets it, so no script there can read it.
 *
 * `main.tsx` takes it before the router or error reporting start, and the page
 * takes it again to read it, so a page reached any other way is covered too.
 * What error reporting hears of the address after it starts, it hears through
 * {@link withoutSetPasswordToken}.
 */

/** `/<label>/set-password`, the page's route (`routes/$label_.set-password.tsx`). */
const SET_PASSWORD_PATH = /^\/[^/]+\/set-password\/?$/;
const STORAGE_KEY = "setPasswordToken";

/** The newest token, when the tab refused to store it. */
let unstored = "";

/**
 * Take a set-password link's token off the address, if this is the page and the
 * address carries one, and answer the token this tab holds: empty when none.
 * On any other page the tab holds none, and that page's fragment is left where
 * it is.
 */
export function takeSetPasswordToken(): string {
	const { hash, pathname, search } = window.location;
	if (!SET_PASSWORD_PATH.test(pathname)) {
		forgetSetPasswordToken();
		return "";
	}
	if (hash) {
		const token = hash.slice(1);
		try {
			sessionStorage.setItem(STORAGE_KEY, token);
			unstored = "";
		} catch {
			unstored = token;
			forgetStored();
		}
		window.history.replaceState(window.history.state, "", pathname + search);
	}
	if (unstored) return unstored;
	try {
		return sessionStorage.getItem(STORAGE_KEY) ?? "";
	} catch {
		return "";
	}
}

const forgetStored = (): void => {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// Nothing was stored.
	}
};

/** Forget the token, once it is spent. */
export function forgetSetPasswordToken(): void {
	unstored = "";
	forgetStored();
}

/**
 * Forget the token if the tab has left the page, as the page's own unmount
 * does. A reload of the page, or the page mounted again where it is, keeps it.
 */
export function forgetSetPasswordTokenIfLeft(): void {
	if (!SET_PASSWORD_PATH.test(window.location.pathname)) {
		forgetSetPasswordToken();
	}
}

/** A scheme and host at the start of an absolute address. */
const ORIGIN = /^[a-z][a-z0-9+.-]*:\/\/[^/]*/i;

/**
 * The address without a set-password link's token, for whatever records
 * addresses once the page is open (`lib/sentry.ts`). Only a set-password
 * address loses its fragment: the desktop client's routes live in theirs.
 * Absolute and path-only addresses alike.
 */
export const withoutSetPasswordToken = (address: string): string => {
	const fragmentAt = address.indexOf("#");
	if (fragmentAt === -1) return address;
	const beforeFragment = address.slice(0, fragmentAt);
	const [path = ""] = beforeFragment.replace(ORIGIN, "").split("?");
	return SET_PASSWORD_PATH.test(path) ? beforeFragment : address;
};
